import { FC, useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from 'components/UI/Button';
import { InputField } from 'components/UI/InputField';
import { Modal } from 'components/UI/Modal';
import { SelectField } from 'components/UI/SelectField';
import { useTranslations } from 'hooks/useTranslations';
import { Country, ExtendedUser } from 'models/diam';
import { useUpdateUserMutation } from 'store/diamApi';
import { addressRegex, cityRegex, postalCodeRegex } from 'utils/constants';
import { showSuccessToast } from 'utils/toastUtils';
import { InputFieldContainer } from './styled';

type Props = {
  user: ExtendedUser;
  countries: Country[];
  isOpen: boolean;
  close: () => void;
};

export const EditAddressModal: FC<Props> = ({
  user,
  countries,
  isOpen,
  close
}) => {
  const t = useTranslations();
  const [updateUser] = useUpdateUserMutation();

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        country: z.string().min(1, t('validation.required')),
        address: z
          .string()
          .max(100, t('validation.maximum_length', { length: 100 }))
          .regex(addressRegex, t('validation.address'))
          .or(z.literal('')),
        address2: z
          .string()
          .max(100, t('validation.maximum_length', { length: 100 }))
          .regex(addressRegex, t('validation.address'))
          .or(z.literal('')),
        postalCode: z
          .string()
          .max(50, t('validation.maximum_length', { length: 50 }))
          .regex(postalCodeRegex, t('validation.postal_code'))
          .or(z.literal('')),
        city: z
          .string()
          .max(50, t('validation.maximum_length', { length: 50 }))
          .regex(cityRegex, t('validation.city'))
          .or(z.literal(''))
      }),
    [t]
  );

  type FormData = z.infer<typeof schema>;

  // useForm
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Close
  const onClose = useCallback(() => close(), [close]);

  // Submit
  const onSubmit: SubmitHandler<FormData> = useCallback(
    ({ address, address2, city, postalCode, country }) => {
      updateUser({
        country,
        postalAddress: address.trim(),
        address2: address2.trim(),
        postalCode: postalCode.trim(),
        city: city.trim()
      });
      showSuccessToast(t('profile.address_update_successful'));
      onClose();
    },
    [onClose, t, updateUser]
  );

  // Setting form value for country
  const onSelectCountry = useCallback(
    (value: string) => {
      setValue('country', value);
    },
    [setValue]
  );

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.countryCode,
        label: country.name
      })),
    [countries]
  );

  return (
    <Modal
      title={t('profile.action_edit_address')}
      isOpen={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFieldContainer>
          <SelectField
            id="country"
            label={t('profile.label_country')}
            required
            enableSearch
            noMatchText={t('general.no_match_found')}
            defaultValue={user.country}
            placeholder={t('profile.placeholder_country')}
            error={errors.country}
            options={countryOptions}
            onSelectValue={onSelectCountry}
            register={register('country')}
          />
          <InputField
            id="address"
            label={t('profile.label_address')}
            defaultValue={user.postalAddress}
            placeholder={t('profile.placeholder_address')}
            error={errors.address}
            register={register('address')}
          />
          <InputField
            id="address2"
            label={t('profile.label_address2')}
            defaultValue={user.address2}
            placeholder={t('profile.placeholder_address2')}
            error={errors.address2}
            register={register('address2')}
          />
          <InputField
            id="postalCode"
            label={t('profile.label_postal_code')}
            defaultValue={user.postalCode}
            placeholder={t('profile.placeholder_postal_code')}
            error={errors.postalCode}
            register={register('postalCode')}
          />
          <InputField
            id="city"
            label={t('profile.label_city')}
            defaultValue={user.city}
            placeholder={t('profile.placeholder_city')}
            error={errors.city}
            register={register('city')}
          />
        </InputFieldContainer>
        <Button type="submit">{t('profile.save')}</Button>
      </form>
    </Modal>
  );
};
